import { useFormikContext } from 'formik'
import React from 'react'

import { CopyButton } from '@components/common/CopyButton'
import { SideSummary } from '@components/common/SideSummary'
import { PriceString } from '@fourthwall/components'

import { useCurrentShop } from '../../../../../../hooks/useCurrentShop'
import type { FormValues, GiftCard } from '../../GiftCard.types'
import { isGiftCardGroup } from '../../utils/isGiftCardGroup'
import { isGiftCardSingle } from '../../utils/isGiftCardSingle'
import * as S from './GiftCardSummary.styled'

type GiftCardSummaryProps = {
  isCreating: boolean
  giftCard?: GiftCard
}

export const GiftCardSummary = ({ isCreating, giftCard }: GiftCardSummaryProps) => {
  const { values, errors } = useFormikContext<FormValues>()
  const { currentShopQuery } = useCurrentShop()

  const renderSummaryContent = () => {
    if (
      ((values.type === 'SINGLE' && !values.code) ||
        (values.type === 'MULTIPLE' && !values.codes?.length)) &&
      (!values.amount || errors.amount)
    ) {
      return <>Enter information on the left to see a summary of your gift card here.</>
    }

    const giftCardBalanceUri =
      currentShopQuery.data && giftCard && 'id' in giftCard
        ? `${currentShopQuery.data?.baseUri}/gift_cards/${giftCard.id}`
        : undefined

    return (
      <>
        {!isCreating && values.type === 'SINGLE' && values.code && (
          <SideSummary.Item title="Code to use at checkout:">
            <S.GiftCardCodeWrapper>
              {giftCard && isGiftCardSingle(giftCard) && (
                <CopyButton
                  href={giftCardBalanceUri}
                  label={giftCard.code}
                  copyText={giftCard.code}
                  iconSize={16}
                />
              )}
            </S.GiftCardCodeWrapper>
          </SideSummary.Item>
        )}
        {!isCreating && values.type === 'MULTIPLE' && (
          <SideSummary.Item title="Number of gift card codes:">
            {values.codes.length}{' '}
            {giftCard && isGiftCardGroup(giftCard) && (
              <>
                ({giftCard.giftCards.filter(({ status }) => status === 'FULLY_USED').length} used)
              </>
            )}
          </SideSummary.Item>
        )}
        {values.amount && !errors.amount && (
          <SideSummary.Item title="Gift card value:">
            <PriceString
              value={values.amount}
              currency={currentShopQuery?.data?.settings.currency}
            />
            {giftCard && isGiftCardSingle(giftCard) && (
              <>
                {' '}
                (used <PriceString {...giftCard.balance.used} />)
              </>
            )}
          </SideSummary.Item>
        )}
      </>
    )
  }

  return <SideSummary>{renderSummaryContent()}</SideSummary>
}
